import { IconButton, Paper, Stack, Typography, useTheme } from "@mui/material";
import { CloseOutlined, DragIndicatorOutlined } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Modal arrastável e redimensionável, sem backdrop: o mapa continua usável.
// Tamanho/posição (storageKey) e aberto/fechado (usePersistentOpen) sobrevivem ao F5.

const MIN_W = 320;
const MIN_H = 300;
const MARGIN = 16;

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// encaixa a box na viewport atual (janela pode ter diminuído)
const fitBox = (b) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = clamp(b.w, MIN_W, vw - MARGIN);
  const h = clamp(b.h, MIN_H, vh - MARGIN);
  return {
    w,
    h,
    x: clamp(b.x, 0, vw - w),
    y: clamp(b.y, 0, vh - h),
  };
};

const readJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

// por padrão abre encostado à direita
const defaultBoxRight = (w = 380) => ({
  w,
  h: window.innerHeight - 140,
  x: window.innerWidth - w - MARGIN,
  y: 80,
});

export const usePersistentOpen = (key) => {
  const [open, setOpen] = useState(() => [true, 1].includes(readJson(key)));
  useEffect(() => writeJson(key, open), [key, open]);
  return [open, setOpen];
};

const DraggableModal = ({
  open,
  onClose,
  title,
  storageKey,
  defaultBox = defaultBoxRight,
  children,
}) => {
  const theme = useTheme();
  const [box, setBox] = useState(null);
  const paperRef = useRef(null);
  const boxRef = useRef(null);
  boxRef.current = box;

  useEffect(() => {
    if (open) setBox(fitBox({ ...defaultBox(), ...readJson(storageKey) }));
    // defaultBox só vale na abertura
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, storageKey]);

  // reencaixa ao redimensionar a janela; boxRef tem o tamanho real (resize do CSS)
  useEffect(() => {
    if (!open) return;
    const onResize = () => {
      if (!boxRef.current) return;
      const next = fitBox(boxRef.current);
      boxRef.current = next;
      // o state pode já ter esse valor enquanto o DOM tem o tamanho do resize
      // manual, então o React não reaplicaria o style: aplica direto
      const el = paperRef.current;
      if (el) {
        el.style.width = `${next.w}px`;
        el.style.height = `${next.h}px`;
      }
      setBox(next);
      writeJson(storageKey, next);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open, storageKey]);

  // o redimensionamento é o `resize` nativo do CSS; aqui só persiste o tamanho
  const hasBox = box !== null;
  useEffect(() => {
    const el = paperRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      if (!boxRef.current) return;
      boxRef.current = {
        ...boxRef.current,
        w: el.offsetWidth,
        h: el.offsetHeight,
      };
      writeJson(storageKey, boxRef.current);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [open, hasBox, storageKey]);

  // a roda do mouse sobre o modal nunca rola a página/mapa por trás. O scroll
  // é feito aqui: sobe a partir do alvo até achar uma área que ainda consegue
  // rolar nesse sentido (ignora as que já estão no limite ou só "parecem"
  // roláveis, como o scroller das abas do MUI) e rola ela.
  useEffect(() => {
    const paper = paperRef.current;
    if (!paper) return;
    const onWheel = (e) => {
      if (e.ctrlKey) return; // pinch/zoom do navegador
      e.preventDefault();
      e.stopPropagation();
      // deltaMode 1 = linhas, 2 = páginas
      const dy =
        e.deltaMode === 1
          ? e.deltaY * 16
          : e.deltaMode === 2
            ? e.deltaY * 400
            : e.deltaY;
      if (!dy) return;
      for (let el = e.target; el && el !== paper; el = el.parentElement) {
        const { overflowY } = getComputedStyle(el);
        if (overflowY !== "auto" && overflowY !== "scroll") continue;
        const max = el.scrollHeight - el.clientHeight;
        const canScroll = dy < 0 ? el.scrollTop > 0 : el.scrollTop < max - 1;
        if (max > 1 && canScroll) {
          el.scrollTop += dy;
          return;
        }
      }
    };
    // passive: false para poder chamar preventDefault
    paper.addEventListener("wheel", onWheel, { passive: false });
    return () => paper.removeEventListener("wheel", onWheel);
  }, [open, hasBox]);

  const handleDragStart = (e) => {
    if (e.button !== 0 || e.target.closest("button")) return;
    e.preventDefault();
    const start = { mx: e.clientX, my: e.clientY, ...boxRef.current };

    const onMove = (ev) => {
      const w = paperRef.current?.offsetWidth ?? start.w;
      const h = paperRef.current?.offsetHeight ?? start.h;
      setBox({
        w,
        h,
        x: clamp(start.x + ev.clientX - start.mx, 0, window.innerWidth - w),
        y: clamp(start.y + ev.clientY - start.my, 0, window.innerHeight - h),
      });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      writeJson(storageKey, boxRef.current);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  if (!open || !box) return null;

  return createPortal(
    <Paper
      ref={paperRef}
      elevation={8}
      role="dialog"
      aria-label={title}
      // valores que mudam a cada mousemove ficam no style, não no sx (evita gerar classes)
      style={{ left: box.x, top: box.y, width: box.w, height: box.h }}
      sx={{
        position: "fixed",
        zIndex: 1501,
        minWidth: MIN_W,
        minHeight: MIN_H,
        maxWidth: `calc(100vw - ${box.x}px)`,
        maxHeight: `calc(100vh - ${box.y}px)`,
        resize: "both",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={0.75}
        onMouseDown={handleDragStart}
        sx={{
          pl: 1.5,
          pr: 0.5,
          py: 0.75,
          cursor: "grab",
          userSelect: "none",
          "&:active": { cursor: "grabbing" },
        }}
      >
        <DragIndicatorOutlined
          fontSize="small"
          sx={{ color: theme.palette.text.disabled }}
        />
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 500,
            flexGrow: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </Typography>
        <IconButton size="small" aria-label="Fechar" onClick={onClose}>
          <CloseOutlined fontSize="small" />
        </IconButton>
      </Stack>
      {children}
    </Paper>,
    document.body,
  );
};

export default DraggableModal;
